import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { InvoiceService } from '../invoice.service';
import {
  HLM_DIALOG_SHELL,
  HlmBtnDirective,
  HlmIconComponent,
  HlmSpinnerComponent,
  HlmAlertComponent,
} from '../../../ui';

export interface EvnPdfDialogData {
  invoiceId: number;
  invoiceNumber: number;
}

@Component({
  selector: 'cmt-evn-pdf-dialog',
  imports: [
    ...HLM_DIALOG_SHELL,
    HlmBtnDirective,
    HlmIconComponent,
    HlmSpinnerComponent,
    HlmAlertComponent,
  ],
  template: `
    <hlm-dialog-surface size="xl" class="!max-h-[92vh] !h-[92vh] !w-[90vw] !max-w-[90vw]">
      <hlm-dialog-header>
        <hlm-dialog-icon-badge tone="primary">
          <hlm-icon name="lucideFileText" size="1.1rem" />
        </hlm-dialog-icon-badge>
        <div hlmDialogHeading>
          <h2 hlmDialogTitle>EVN of invoice {{ data.invoiceNumber }}</h2>
          <p hlmDialogDescription>Individual call record (Einzelverbindungsnachweis).</p>
        </div>
        <button hlmDialogClose (click)="dialogRef.close()">
          <hlm-icon name="lucideX" size="1rem" />
        </button>
      </hlm-dialog-header>

      <section hlmDialogBody class="flex min-h-0 flex-1 flex-col">
        @if (loading()) {
          <div class="flex flex-1 items-center justify-center gap-3 text-sm text-muted-foreground">
            <hlm-spinner size="24" />
            Loading PDF…
          </div>
        } @else if (errorMsg()) {
          <hlm-alert tone="error">
            <hlm-icon name="lucideCircleAlert" size="1rem" class="mt-0.5 text-destructive" />
            <span>{{ errorMsg() }}</span>
          </hlm-alert>
        } @else if (pdfUrl()) {
          <iframe
            [src]="pdfUrl()!"
            class="flex-1 rounded-lg border border-border"
            title="EVN PDF"
          ></iframe>
        }
      </section>

      <footer hlmDialogFooter>
        <button hlmBtn variant="outline" (click)="dialogRef.close()">Close</button>
      </footer>
    </hlm-dialog-surface>
  `,
})
export class EvnPdfDialogComponent implements OnInit, OnDestroy {
  readonly data = inject<EvnPdfDialogData>(MAT_DIALOG_DATA);
  readonly dialogRef = inject(MatDialogRef<EvnPdfDialogComponent>);
  private readonly invoiceService = inject(InvoiceService);
  private readonly sanitizer = inject(DomSanitizer);

  readonly loading = signal(true);
  readonly errorMsg = signal<string | null>(null);
  readonly pdfUrl = signal<SafeResourceUrl | null>(null);

  private blobUrl: string | null = null;

  ngOnInit(): void {
    this.invoiceService.getEvnPdf(this.data.invoiceId).subscribe({
      next: (blob) => {
        this.blobUrl = URL.createObjectURL(blob);
        this.pdfUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(this.blobUrl));
        this.loading.set(false);
      },
      error: () => {
        this.errorMsg.set('Failed to load EVN PDF');
        this.loading.set(false);
      },
    });
  }

  ngOnDestroy(): void {
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
    }
  }
}
